// Single place that reads or writes the session token.
//
// WHAT THIS DOES: keeps the token out of localStorage (so it does not outlive
// the tab) and stores it as AES-GCM ciphertext rather than plaintext.
//
// WHAT THIS DOES NOT DO: protect the token from XSS. The decryption key is
// derived from REACT_APP_ENCRYPTION_KEY, which CRA compiles into the JS bundle
// and is therefore readable by anyone — including any script running on the
// page. This is obfuscation against casual sessionStorage inspection plus a
// shorter persistence window. It is not a control against a compromised
// browser. The real fix is an HttpOnly cookie, which is out of scope.
//
// The stored value is opaque to this module: today it is the JWT envelope that
// sessionActions writes (payload = the whole login response, with the bearer
// token on `.token`). Callers keep doing their own jwt_decode. That keeps this
// module valid regardless of how the login/refresh response shape question is
// settled.

import jwt_decode from 'jwt-decode';
import { deriveScopedKeyB64, encryptPayload, decryptResponse } from '../crypto/cryptoAesGcm';

const STORAGE_KEY = 'cpToken';
const LEGACY_PROFILE_KEYS = [
  '_accountName',
  '_contactName',
  '_portalPermissions',
  '_userName',
];

export interface PortalSession {
  token: string;
  exp?: number;
  [key: string]: any;
}

// Plaintext token held in memory after boot, so every read is synchronous and
// we decrypt once rather than per request. Axios interceptors can be async, so
// this is for simplicity and cost, not necessity.
let cached: string | null = null;

// Bumped by every clearToken(). persist() captures it before awaiting the two
// crypto calls and refuses to write if it has moved, so a logout that lands
// mid-encryption cannot be undone by the write it interrupted.
//
// This matters because the racing clearToken() is usually not a user clicking
// logout — it is a concurrent request handling a 401 (navActions) or the
// interceptor's endExpiredSession. Those fire in reaction to the same token
// expiry that triggered the refresh, so they are correlated by construction,
// not independent events. Without the check the pending write restores the
// session, restoreToken() then succeeds on the next boot, and the user is
// redirected back into the app holding a token the server already rejected.
let generation = 0;

// Memoised parse of `cached`. getSession() is called by the axios request
// interceptor, so without this every request re-parses the whole login
// response. Keyed on the raw string, and invalidated by every write, so it
// can never return a session that does not match the token in memory.
let parsedRaw: string | null = null;
let parsedSession: any | null = null;

function readStorage(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
}

export function safeRemoveStorageItem(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch (e) {
    // Storage can be disabled by browser/privacy policy. The in-memory token
    // is still cleared by the caller, so logout continues to fail closed.
  }
}

// The only place `cached` is written. Keeps the parse cache from going stale.
function setCachedToken(raw: string | null): void {
  cached = raw;
  parsedRaw = null;
  parsedSession = null;
}

/**
 * Reads and decrypts the stored token into memory. Resolves true when a usable
 * token was restored.
 *
 * Call once during app startup and AWAIT it before dispatching anything that
 * can issue an API request — `getToken()` returns null until this resolves, so
 * a request racing startup would go out unauthenticated.
 *
 * Also migrates a pre-existing plaintext `localStorage.cpToken` from the old
 * scheme, so users already logged in when this ships are not signed out. The
 * legacy copy is removed once carried across.
 *
 */
export async function restoreToken(): Promise<boolean> {
  // Migrate a legacy plaintext token, if one is still sitting in localStorage.
  const legacy = readStorage(localStorage, STORAGE_KEY);
  if (legacy) {
    if (!readStorage(sessionStorage, STORAGE_KEY)) {
      try {
        await setToken(legacy);
        if (!getAccessToken()) {
          clearToken();
          return false;
        }
        safeRemoveStorageItem(localStorage, STORAGE_KEY);
        return true;
      } catch (e) {
        // Encryption unavailable (no key, or insecure context). Drop the token
        // and make the user log in again rather than fall back to plaintext.
        clearToken();
        return false;
      }
    } else {
      // A secure copy already exists, so the plaintext legacy copy is no
      // longer needed.
      safeRemoveStorageItem(localStorage, STORAGE_KEY);
    }
  }

  const stored = readStorage(sessionStorage, STORAGE_KEY);
  if (!stored) {
    setCachedToken(null);
    return false;
  }

  try {
    const keyB64 = await deriveScopedKeyB64();
    setCachedToken(await decryptResponse<string>(stored, keyB64));
    if (!getAccessToken()) {
      clearToken();
      return false;
    }
    return true;
  } catch (e) {
    // Any of: wrong key (different tenant domain), tampered ciphertext,
    // truncated value with no usable IV, or plain corruption. AES-GCM fails
    // closed — decrypt throws rather than returning partial plaintext — so
    // there is one path for all of them: treat as not-logged-in and clear.
    clearToken();
    return false;
  }
}

/**
 * The token, or null. Synchronous — returns what `restoreToken()` put in
 * memory, so `restoreToken()` must have resolved first.
 */
export function getToken(): string | null {
  return cached;
}

/**
 * Encrypts and stores the token, then updates the in-memory copy.
 *
 * Order matters: the cache is written last, so if encryption or the
 * sessionStorage write throws (no key, insecure context, quota) we do not end
 * up believing we hold a token that was never persisted.
 */
async function persist(raw: string): Promise<void> {
  const writeGeneration = generation;
  const keyB64 = await deriveScopedKeyB64();
  const encrypted = await encryptPayload(raw, keyB64);
  if (writeGeneration !== generation) {
    // Throw rather than return quietly: persistAuthenticatedSession() and the
    // refresh paths treat a rejection as failure and fail closed, so the
    // caller will not go on to restore the Authorization header and the
    // authenticated Redux state that logout just tore down.
    throw new Error('Session was cleared while it was being stored.');
  }
  sessionStorage.setItem(STORAGE_KEY, encrypted);
  setCachedToken(raw);
  // These values were written by the old login flow. They are no longer read
  // by the application and include PII/permissions, so remove stale copies
  // only after the encrypted session has been saved successfully.
  LEGACY_PROFILE_KEYS.forEach((key) => safeRemoveStorageItem(localStorage, key));
}

/** Stores one stable representation for login, reload, and refresh. */
export async function setSession(session: PortalSession): Promise<void> {
  if (!session || typeof session.token !== 'string' || !session.token) {
    throw new Error('Cannot store a portal session without an access token.');
  }
  await persist(JSON.stringify(session));
}

/**
 * Legacy-compatible raw writer used only while migrating an existing
 * localStorage value. New application code should call setSession().
 */
export async function setToken(token: string): Promise<void> {
  await persist(token);
}

/**
 * The decoded session object — the login response, whose `.token` is the
 * bearer token and whose `.exp` drives the refresh check. Null when not
 * logged in or when the stored value is unreadable.
 *
 * TRANSITIONAL: accepts the old JWT envelope so existing users can migrate.
 *
 *  - JSON, written by `login` / `loginByToken` after the hard-coded
 *    `jwt.encode(res.data, "fe1a1915...")` envelope was removed.
 *  - A JWT envelope, still written by both refresh paths.
 *
 * The two are trivially distinguishable (JSON starts with `{`, a JWT is three
 * dot-separated base64 segments), so this is a reliable check rather than a
 * guess. Once the advance-api login/refresh contract is documented and the
 * refresh paths are aligned, drop the jwt_decode branch.
 */
export function getSession(): any | null {
  const raw = getToken();
  if (!raw) return null;
  if (raw === parsedRaw) return parsedSession;

  let session: any | null = null;
  try {
    session = JSON.parse(raw);
  } catch (e) {
    // Not JSON — fall through to the legacy JWT envelope.
    try {
      session = jwt_decode(raw);
    } catch (e2) {
      session = null;
    }
  }

  parsedRaw = raw;
  parsedSession = session;
  return session;
}

export function getAccessToken(): string | null {
  const session = getSession();
  return session && typeof session.token === 'string' ? session.token : null;
}

/** Updates the bearer token without replacing the portal user/session data. */
export async function updateAccessToken(token: string): Promise<PortalSession> {
  if (typeof token !== 'string' || !token) {
    throw new Error('Refresh response did not contain an access token.');
  }

  const current = getSession();
  if (!current) {
    throw new Error('Cannot refresh a missing portal session.');
  }

  let claims: any = {};
  try {
    claims = jwt_decode(token) || {};
  } catch (e) {
    // Some environments may return an opaque bearer token. Keep the rest of
    // the portal session; `exp` is dealt with below.
  }

  const updated: PortalSession = {
    ...current,
    ...claims,
    // A bare-string refresh response can itself be the legacy JWT envelope
    // whose decoded payload contains the actual bearer on `.token`. Prefer
    // that nested value; for an ordinary JWT or opaque token, use the refresh
    // response value unchanged.
    token: typeof claims.token === 'string' && claims.token ? claims.token : token,
  };

  // The refresh we just completed was triggered by `current.exp` being in the
  // past. If the new token carries no expiry of its own — an opaque bearer
  // token, which this function deliberately supports — carrying the old value
  // over would leave the session permanently "expired" and make the request
  // interceptor refresh before every single subsequent request. Drop it
  // instead: an unknown expiry means we stop pre-emptively refreshing and let
  // the API reject the token when it actually expires.
  if (typeof claims.exp !== 'number') {
    delete updated.exp;
  }

  await setSession(updated);
  return updated;
}

/** Supports both refresh shapes currently used by the two legacy call sites. */
export function extractRefreshToken(responseData: any): string | null {
  const data = responseData && responseData.data;
  if (typeof data === 'string' && data) return data;
  if (data && typeof data.token === 'string' && data.token) return data.token;
  return null;
}

/** Wipes the token from memory and from storage (both old and new locations). */
export function clearToken(): void {
  generation += 1;
  setCachedToken(null);
  safeRemoveStorageItem(sessionStorage, STORAGE_KEY);
  safeRemoveStorageItem(localStorage, STORAGE_KEY);
  LEGACY_PROFILE_KEYS.forEach((key) => safeRemoveStorageItem(localStorage, key));
}
